import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { createReview, deleteReview as apiDeleteReview } from '../api';
import { useAppConfig } from '../state/config';
import { useProfile } from '../state/profile';
import type {
  CompletedReviewStatus,
  Geography,
  Review,
  ReviewStatus,
} from '../types';
import { GEO_ALL } from '../types';

/**
 * Per-geography status chips with an inline picker popover.
 *
 * Clicking a chip opens a small menu for that single geography; selecting a
 * status saves immediately (POST for a completed status, DELETE to revert to
 * pending) and closes. The multi-geography dialog is reserved for the
 * "Modify review status" button on the form detail page.
 */
export function ReviewBadges({
  kbNumber,
  reviewsByGeo,
  topBarGeo,
  stream,
  formSysId,
  onChanged,
  size = 'sm',
}: {
  kbNumber: string;
  reviewsByGeo: Map<string, Review> | undefined;
  topBarGeo: string;
  stream: string | null;
  formSysId: string | null;
  onChanged: () => void;
  size?: 'sm' | 'md';
}) {
  const { geographies } = useAppConfig();

  const visibleGeos = useMemo<Geography[]>(() => {
    if (topBarGeo === GEO_ALL) return geographies;
    const only = geographies.find((g) => g.id === topBarGeo);
    return only ? [only] : [];
  }, [geographies, topBarGeo]);

  if (visibleGeos.length === 0) return null;

  return (
    <div className="inline-flex items-center gap-1 flex-wrap">
      {visibleGeos.map((geo) => {
        const status = reviewsByGeo?.get(geo.id)?.status ?? 'pending_review';
        return (
          <StatusChip
            key={geo.id}
            kbNumber={kbNumber}
            stream={stream}
            formSysId={formSysId}
            geo={geo}
            status={status}
            size={size}
            onChanged={onChanged}
          />
        );
      })}
    </div>
  );
}

export const STATUS_ICON: Record<ReviewStatus, string> = {
  pending_review: '⏳',
  review_completed_form_required: '✅',
  review_completed_form_not_required: '🚫',
};

export const STATUS_LABEL: Record<ReviewStatus, string> = {
  pending_review: 'Pending review',
  review_completed_form_required: 'Review completed — form required',
  review_completed_form_not_required: 'Review completed — form not required',
};

/** Tailwind classes for the status colour scheme. Shared by chips + dropdowns. */
export function statusChipClass(status: ReviewStatus): string {
  switch (status) {
    case 'review_completed_form_required':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'review_completed_form_not_required':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'pending_review':
    default:
      return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}

const STATUS_OPTIONS: ReviewStatus[] = [
  'pending_review',
  'review_completed_form_required',
  'review_completed_form_not_required',
];

function StatusChip({
  kbNumber,
  stream,
  formSysId,
  geo,
  status,
  size,
  onChanged,
}: {
  kbNumber: string;
  stream: string | null;
  formSysId: string | null;
  geo: Geography;
  status: ReviewStatus;
  size: 'sm' | 'md';
  onChanged: () => void;
}) {
  const { profile } = useProfile();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(
    null,
  );
  const [saving, setSaving] = useState<ReviewStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Outside click + Escape dismiss.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      if (
        popoverRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close on scroll / resize so the popover doesn't drift from its anchor.
  useEffect(() => {
    if (!open) return;
    const onMove = () => setOpen(false);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Prefer right-aligning the popover with the chip, but clamp so it never
    // overflows the left or right edge of the viewport. Uses the popover's
    // max width (≈ 352px) since we can't measure before render.
    const margin = 8;
    const estWidth = Math.min(352, window.innerWidth - margin * 2);
    const preferredLeft = rect.right - estWidth;
    const maxLeft = window.innerWidth - estWidth - margin;
    const left = Math.max(margin, Math.min(preferredLeft, maxLeft));
    setAnchor({ left, top: rect.bottom + 4 });
    setOpen(true);
  }

  async function pick(next: ReviewStatus) {
    if (saving) return;
    if (next === status) {
      setOpen(false);
      return;
    }
    setError(null);
    setSaving(next);
    try {
      if (next === 'pending_review') {
        await apiDeleteReview(kbNumber, geo.id);
      } else {
        await createReview({
          kb_number: kbNumber,
          geography: geo.id,
          status: next as CompletedReviewStatus,
          reviewer_name: profile?.full_name ?? 'Unknown',
          reviewer_email: profile?.email ?? null,
          stream,
          form_sys_id: formSysId,
        });
      }
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  const padClass =
    size === 'sm' ? 'px-1.5 py-0 text-[11px] h-5' : 'px-2 py-0.5 text-xs h-6';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={
          'inline-flex items-center gap-1 rounded-full font-mono font-medium border transition hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 ' +
          statusChipClass(status) +
          ' ' +
          padClass +
          (open ? ' ring-2 ring-offset-1 ring-slate-400' : '')
        }
        title={`${geo.name} · ${STATUS_LABEL[status]} (click to change)`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggle();
        }}
      >
        <span aria-hidden className="leading-none">
          {STATUS_ICON[status]}
        </span>
        <span>{geo.code}</span>
      </button>
      {open &&
        anchor &&
        createPortal(
          <div
            ref={popoverRef}
            role="menu"
            className="fixed z-40 w-max min-w-[18rem] max-w-[min(22rem,calc(100vw-16px))] bg-white border border-slate-200 shadow-card rounded-md py-1 text-sm text-slate-700"
            style={{ left: anchor.left, top: anchor.top }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              {geo.name}
            </div>
            {STATUS_OPTIONS.map((s) => {
              const isCurrent = s === status;
              const isSaving = saving === s;
              return (
                <button
                  key={s}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isCurrent}
                  disabled={saving !== null}
                  className={
                    'w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-wait transition ' +
                    (isCurrent ? 'bg-slate-50' : '')
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    pick(s);
                  }}
                >
                  <span aria-hidden className="leading-none w-4 text-center">
                    {STATUS_ICON[s]}
                  </span>
                  <span className="flex-1 whitespace-nowrap">
                    {STATUS_LABEL[s]}
                  </span>
                  {isSaving ? (
                    <span className="text-slate-400 text-xs">…</span>
                  ) : isCurrent ? (
                    <span className="text-slate-400 text-xs">✓</span>
                  ) : null}
                </button>
              );
            })}
            {error && (
              <div className="mt-1 mx-2 mb-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                {error}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
